import classNames from 'classnames';
import { cloneElement, type JSX } from 'react';
import { DropdownMenuItem } from '../../Dropdown/DropdownMenuItem';
import type { DiNavItemBaseProps } from '../DiNavItem/DiNavItem.types';
import type { DiNavDropdownMenuProps } from './DiNavDropdownMenu.types';

/**
 * A dropdown menu component designed for use within `DiNavDropdown`.
 *
 * Renders a vertical menu (`<ul>`) containing a list of navigation items (`<li>`).
 * This component handles the presentation and interaction of dropdown menu items.
 *
 * **Note**: This functionality could have been integrated directly into DiNavDropdown,
 * but was separated into its own component to accommodate the HeaderNavDropdownMenu (designed
 * to facilitate isolated demonstration in Storybook).
 *
 * @example
 * ```tsx
 * <DiNavDropdownMenu
 *   label="More"
 *   currentIndex={2}  // Highlights the third item in the dropdown
 *   items={overflowItems}
 *   menuItem={<HeaderNavDropdownItem />}
 *   dropdownMenuClassName="custom-menu-class"
 * />
 * ```
 */
export function DiNavDropdownMenu({
  items,
  currentIndex = -1,
  dropdownMenuClassName,
  dropdownMenuItem: menuItem,
}: DiNavDropdownMenuProps): JSX.Element {
  return (
    <ul
      role="menu"
      className={classNames(
        'list-none m-dt-spacing-none p-dt-spacing-none',
        dropdownMenuClassName,
      )}
    >
      {items?.map((item, idx) => (
        <li role="none" key={`nav-dropdownmenu-${item.label}-${item.id}`}>
          <DropdownMenuItem
            label={item.label ?? ''}
            onClick={item.onClick}
            renderComponent={({ isActive, itemProps }) => {
              return cloneElement<DiNavItemBaseProps>(menuItem, {
                ...item,
                isCurrent: currentIndex === idx,
                isFocused: isActive,
                ...itemProps,
              });
            }}
          />
        </li>
      ))}
    </ul>
  );
}
